import { TENANT_HEADER_NAME, readStoredTenantId } from "./tenant-isolation";
import { readAddressScope } from "./estate-selection";

/**
 * The tenant the caller is acting in. `requireTenant()` answers 400 without
 * it, so any request that reaches the API needs it, whether or not it goes
 * through `apiFetch`. Shared rather than inlined per caller, because a second
 * fetch path that omitted it is what this fixes.
 */
export function applyScopeHeaders(headers: Headers): Headers {
  if (!headers.has(TENANT_HEADER_NAME)) {
    const tenantId = readStoredTenantId();
    if (tenantId) headers.set(TENANT_HEADER_NAME, tenantId);
  }
  return headers;
}

/** The request parameters the API reads a scope from. */
export const ESTATE_PARAM = "estateId";
export const FLEET_PARAM = "fleetId";

// A route whose path already names its Estate, or its Fleet. `current` is the
// Estate route that names none and resolves the Estate from the request.
const ESTATE_IN_PATH = /^\/estates\/(?!current(?:[/?]|$))[^/?]+/;
const FLEET_IN_PATH = /^\/fleets\/[^/?]+/;

/**
 * A request path with the scope of the tab's address added as visible
 * parameters, so every request says which Estate and Fleet it is about.
 *
 * What a request names for itself stands, because the API refuses a request
 * that names two different Estates or Fleets:
 * - an explicit `estateId` or `fleetId` is never replaced;
 * - a route whose path names its Estate gets nothing, since some of those read
 *   `fleetId` as a filter and one refuses parameters it does not know;
 * - a route whose path names its Fleet gets the Estate but no Fleet, since it
 *   may be another Fleet than the one the bar has selected.
 * A Fleet is only sent beside the Estate that holds it: the API refuses a
 * Fleet from another Estate.
 */
export function withScopeParams(path: string): string {
  const scope = readAddressScope();
  if (!scope) return path;
  const [route, query = ""] = path.split("?", 2);
  if (ESTATE_IN_PATH.test(route)) return path;
  const params = new URLSearchParams(query);
  if (!params.has(ESTATE_PARAM)) params.set(ESTATE_PARAM, scope.estateId);
  if (
    scope.fleetId &&
    !FLEET_IN_PATH.test(route) &&
    !params.has(FLEET_PARAM) &&
    params.get(ESTATE_PARAM) === scope.estateId
  )
    params.set(FLEET_PARAM, scope.fleetId);
  return `${route}?${params}`;
}
